/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SanTagInput.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the certificates module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { X } from 'lucide-react';

interface SanTagInputProps {
  /** Current list of SAN domain strings */
  values: string[];
  /** Called when the list changes (add or remove) */
  onChange: (values: string[]) => void;
  /** When true, the input is read-only (e.g. edit mode) */
  disabled?: boolean;
}

/**
 * SanTagInput
 *
 * Renders an interactive tag input where each tag represents a Subject
 * Alternative Name (domain). Users add entries by typing and pressing
 * Enter or comma, and remove them with the X button or Backspace.
 */
export function SanTagInput({ values, onChange, disabled = false }: SanTagInputProps) {
  const [inputValue, setInputValue] = useState('');

  /** Commits the current input value as a new tag if it is non-empty and unique */
  const commitTag = () => {
    const trimmed = inputValue.trim().replace(/,/g, '');
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      // Remove last tag when pressing Backspace on an empty input
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      className={`flex flex-wrap gap-1 p-2 min-h-[38px] border rounded-md bg-background ${
        disabled
          ? 'bg-muted cursor-not-allowed'
          : 'focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary'
      }`}
    >
      {/* Existing tags */}
      {values.map((san, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
        >
          {san}
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(values.filter((_, i) => i !== index))}
              className="hover:text-red-600 transition-colors ml-0.5"
              aria-label={`Remove ${san}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}

      {/* Text input for new tag */}
      {!disabled && (
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitTag}
          placeholder={values.length === 0 ? 'Type domain and press Enter' : ''}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
        />
      )}
    </div>
  );
}
